import { getExaminerProfileThunk } from '../../../../../store/workspace/thunks';
import { ForwardedRef, RefObject, forwardRef, useMemo, useState } from 'react';
import { setExaminerProfileModal } from '../../../../../store/workspace/slice';
import { OverallResultStatics } from '../../../../../store/workspace/types';
import { ChevronDownIcon, ProfileIcon } from '../../../../../assets/icons';
import { useDispatch } from '../../../../../store/store';
import styles from './style.module.scss';

interface Props {
    jobId: string | undefined;
    staticsData: OverallResultStatics | null;
};

const OverallResultsTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { staticsData, jobId } = props;

    const dispatch = useDispatch();
    const [expandedCards, setExpandedCards] = useState<string[]>([]);

    const maxOverallScore = useMemo(() => {
        return (staticsData?.maxRecruitingInterviewScore || 0) +
            (staticsData?.maxMultipleChoiceScore || 0) +
            (staticsData?.maxCodingScore || 0);
    }, [staticsData]);

    const getExaminerProfile = (id: string) => {
        dispatch(getExaminerProfileThunk({ id, jobId }));
        dispatch(setExaminerProfileModal(true));
    };

    const toggleCard = (userId: string) => {
        setExpandedCards(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.table} ref={ref as RefObject<HTMLDivElement>}>
                <table>
                    <thead>
                        <tr data-highlight="true">
                            <th>Name</th>
                            <th>
                                Overall result <br />
                                {maxOverallScore !== 0 && <span className={styles.desc}>(Out of {maxOverallScore})</span>}
                            </th>
                            <th>
                                Recruiting interview result <br />
                                {staticsData?.maxRecruitingInterviewScore && <span className={styles.desc}>(Out of {staticsData.maxRecruitingInterviewScore})</span>}
                            </th>
                            <th>
                                Multiple choice exam result <br />
                                {staticsData?.maxMultipleChoiceScore && <span className={styles.desc}>(Out of {staticsData.maxMultipleChoiceScore})</span>}
                            </th>
                            <th>
                                Coding exam result <br />
                                {staticsData?.maxCodingScore && <span className={styles.desc}>(Out of {staticsData.maxCodingScore})</span>}
                            </th>
                            <th>Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {staticsData?.examiners.map((examiner, index) => (
                            <tr key={index}>
                                <td>{examiner.first_name} {examiner.last_name}</td>
                                <td style={{ color: '#04A87C' }}>{examiner.grade ?? 'N/A'}</td>
                                <td>{examiner.recruitingInterviewGrade ?? 'N/A'}</td>
                                <td>{examiner.multipleChoiceExamGrade ?? 'N/A'}</td>
                                <td>{examiner.codingExamGrade ?? 'N/A'}</td>
                                <td data-noprint="true">
                                    <ProfileIcon onClick={() => getExaminerProfile(examiner.userId)} />
                                </td>
                            </tr>
                        ))}
                        {staticsData?.examiners.length === 0 && <tr>
                            <td colSpan={6} style={{ textAlign: 'center' }}>
                                No data.
                            </td>
                        </tr>}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {staticsData?.examiners.map((examiner, index) => {
                    const isExpanded = expandedCards.includes(examiner.userId);

                    return (
                        <div key={index} className={styles.card}>
                            <div className={styles.card_header} onClick={() => toggleCard(examiner.userId)}>
                                <span>{examiner.first_name} {examiner.last_name}</span>
                                <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                            </div>
                            {isExpanded && <div className={styles.card_body}>
                                <div className={styles.row}>
                                    <span className={styles.label}>Overall result</span>
                                    <span className={styles.value} style={{ color: '#04A87C' }}>{examiner.grade ?? 'N/A'}</span>
                                </div>
                                <div className={styles.row}>
                                    <span className={styles.label}>Recruiting interview result</span>
                                    <span className={styles.value}>{examiner.recruitingInterviewGrade ?? 'N/A'}</span>
                                </div>
                                <div className={styles.row}>
                                    <span className={styles.label}>Multiple choice exam result</span>
                                    <span className={styles.value}>{examiner.multipleChoiceExamGrade ?? 'N/A'}</span>
                                </div>
                                <div className={styles.row}>
                                    <span className={styles.label}>Coding exam result</span>
                                    <span className={styles.value}>{examiner.codingExamGrade ?? 'N/A'}</span>
                                </div>
                            </div>}
                        </div>
                    )
                })}
            </div>
        </div>
    )
});

export default OverallResultsTable;